/*----------------------------------
- DOMAINING NEWS PRESENTATION
----------------------------------*/

/*----------------------------------
- DEPENDENCIES
----------------------------------*/

import {
    DOMAINING_NEWS_INDEX_PATH,
    DOMAINING_NEWS_MAX_PAGE,
    domainingNewsPagePath,
} from '@uniquedomains/components/contracts/news/public';
import {
    USER_PREFERENCE_NEWS_TYPE_IDS,
    type UserPreferenceNewsTypeId,
} from '@uniquedomains/components/contracts/users/preferences';

/*----------------------------------
- CONSTANTS
----------------------------------*/

export const DOMAINING_NEWS_PAGE_SIZE = 12;

export const DOMAINING_NEWS_CATEGORY_QUERY_PARAM = 'category';

/*----------------------------------
- HELPERS
----------------------------------*/

export const resolveDomainingNewsArchivePage = (requestUrl: string): number | null => {
    const pathname = new URL(requestUrl, 'https://unique.domains').pathname.replace(/\/+$/, '');
    if (pathname === DOMAINING_NEWS_INDEX_PATH) return 1;
    const rawPage = pathname.match(/^\/resources\/domaining-news\/page\/([1-9]\d*)$/)?.[1];
    if (!rawPage) return null;

    const page = Number(rawPage);
    return Number.isSafeInteger(page) && page <= DOMAINING_NEWS_MAX_PAGE
        ? page
        : null;
};

export const resolveDomainingNewsDateKey = (requestUrl: string): string => {
    const pathname = new URL(requestUrl, 'https://unique.domains').pathname.replace(/\/+$/, '');
    return pathname.match(/^\/resources\/domaining-news\/(\d{4}-\d{2}-\d{2})$/)?.[1] || '';
};

/**
 * Reads `?category=`. An unknown value degrades to the unfiltered archive rather
 * than 404, because a filter chip is a view, not a resource identity.
 */
export const resolveDomainingNewsCategoryCode = (
    requestUrl: string,
): UserPreferenceNewsTypeId | null => {
    const raw = new URL(requestUrl, 'https://unique.domains').searchParams.get(
        DOMAINING_NEWS_CATEGORY_QUERY_PARAM,
    );
    if (!raw) return null;
    return USER_PREFERENCE_NEWS_TYPE_IDS.includes(raw as UserPreferenceNewsTypeId)
        ? (raw as UserPreferenceNewsTypeId)
        : null;
};

/** Pagination, `rel=prev` and `rel=next` all keep the active filter through here. */
export const buildDomainingNewsArchivePath = (
    page: number,
    categoryCode: UserPreferenceNewsTypeId | null,
): string => {
    const basePath = page <= 1 ? DOMAINING_NEWS_INDEX_PATH : domainingNewsPagePath(page);
    return categoryCode
        ? `${basePath}?${DOMAINING_NEWS_CATEGORY_QUERY_PARAM}=${categoryCode}`
        : basePath;
};

// The bare section name ("Domaining News") told a search result nothing about the
// publication, so the title also says what it is: a daily brief with its sources.
export const DOMAINING_NEWS_ARCHIVE_TITLE = 'Domaining News: Daily Domain Industry Brief with Sources';

export const buildDomainingNewsArchiveTitle = (page: number): string => {
    return page > 1 ? `${DOMAINING_NEWS_ARCHIVE_TITLE}, Page ${page}` : DOMAINING_NEWS_ARCHIVE_TITLE;
};

export const buildDomainingNewsArchiveDescription = (page: number): string => {
    const base =
        'Daily domain-industry news summaries with key takeaways, practical questions and direct citations to original reporting.';
    return page > 1 ? `${base} Archive page ${page}.` : base;
};
